// src/components/Tickets/AttachmentUpload.js
import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import * as indexedDB from '../../utils/indexedDB';
import * as tokenEncryption from '../../utils/tokenEncryption';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

/**
 * Get auth token from secure storage with fallback
 */
const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);
      if (encryptedToken) {
        const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
        if (decryptedToken) {
          return decryptedToken;
        }
      }
    }

    // Fallback to localStorage if IndexedDB fails
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    // Last resort: try localStorage directly
    return localStorage.getItem(key);
  }
};

/**
 * Format bytes to human readable format
 */
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get file icon based on mime type
 */
const getFileIcon = (mimeType) => {
  if (!mimeType) return <FileIcon />;

  if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
  if (mimeType.startsWith('video/')) return <VideoIcon color="secondary" />;
  if (mimeType === 'application/pdf') return <PdfIcon color="error" />;

  return <FileIcon />;
};

/**
 * TicketAttachmentUpload Component
 *
 * Simplified file upload component for ticket attachments that:
 * - Uploads files to the encrypted file system
 * - Returns file_id and download URL
 * - Supports photos, videos, PDFs, and documents
 */
const TicketAttachmentUpload = ({ onFilesUploaded, disabled = false, compact = false }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);

  // File validation configuration
  const MAX_FILE_SIZE = 20 * 1024 * 1024 * 1024; // 20 GB
  const ALLOWED_TYPES = [
    'image/*',
    'video/*',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.*',
    'application/vnd.ms-*',
    'text/*'
  ];

  /**
   * Handle file drop/selection
   */
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    const validFiles = [];
    const newErrors = [];

    acceptedFiles.forEach(file => {
      // Size check
      if (file.size > MAX_FILE_SIZE) {
        newErrors.push(`${file.name}: File too large (max ${formatBytes(MAX_FILE_SIZE)})`);
        return;
      }

      validFiles.push({
        file,
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'pending',
        progress: 0
      });
    });

    rejectedFiles.forEach(({ file, errors: fileErrors }) => {
      fileErrors.forEach(error => {
        newErrors.push(`${file.name}: ${error.message}`);
      });
    });

    setFiles(prev => [...prev, ...validFiles]);
    setErrors(newErrors);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: MAX_FILE_SIZE,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.*': [],
      'application/vnd.ms-*': [],
      'text/*': ['.txt', '.csv']
    },
    disabled: disabled || uploading
  });

  /**
   * Remove file from upload queue
   */
  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  /**
   * Upload files to encrypted file system
   */
  const handleUpload = async () => {
    if (files.length === 0) {
      setErrors(['Please select at least one file to upload']);
      return;
    }

    // Pre-flight check: verify we have a token before starting upload
    const preflightToken = await getTokenFromStorage('auth_token');
    if (!preflightToken) {
      setErrors(['Authentication error: Please refresh the page and log in again.']);
      return;
    }

    setUploading(true);
    setErrors([]);
    const uploadedFiles = [];
    const uploadErrors = [];

    for (const fileItem of files) {
      try {
        // Create FormData for file upload
        const formData = new FormData();

        // Handle mobile camera captures - ensure proper file blob
        let fileToUpload = fileItem.file;

        // If the file is from a mobile camera capture, it might need special handling
        // Check if file has a proper type, if not, try to infer it from the name
        if (!fileToUpload.type && fileToUpload.name) {
          const ext = fileToUpload.name.split('.').pop().toLowerCase();
          const mimeTypes = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'webp': 'image/webp',
            'heic': 'image/heic',
            'mp4': 'video/mp4',
            'mov': 'video/quicktime'
          };

          if (mimeTypes[ext]) {
            // Create a new blob with the correct MIME type
            fileToUpload = new File([fileToUpload], fileToUpload.name, {
              type: mimeTypes[ext],
              lastModified: fileToUpload.lastModified
            });
          }
        }

        formData.append('file', fileToUpload);
        formData.append('classification', 'STANDARD');
        formData.append('category', 'ticket_attachment');
        formData.append('tags', JSON.stringify(['ticket']));

        // Get auth token from secure storage
        const token = await getTokenFromStorage('auth_token');

        console.log('Token retrieved:', token ? 'Yes (length: ' + token.length + ')' : 'No');

        if (!token) {
          throw new Error('Authentication required. Please log in again.');
        }

        // Upload to encrypted file system with longer timeout for large files
        const response = await axios.post(`${API_BASE_URL}/api/files/upload`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type - browser will set it with boundary
          },
          timeout: 3600000, // 1 hour timeout for large files (up to 20GB)
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setFiles(prev => prev.map(f =>
              f.id === fileItem.id ? { ...f, progress } : f
            ));
          }
        });

        // Extract file info from response
        const uploadedFile = {
          file_id: response.data.file.file_id,
          name: response.data.file.name,
          size: response.data.file.size,
          mime_type: response.data.file.mime_type,
          // Generate download URL
          url: `/api/files/${response.data.file.file_id}/download`,
          downloadUrl: `${API_BASE_URL}/api/files/${response.data.file.file_id}/download?inline=false`,
          viewUrl: `${API_BASE_URL}/api/files/${response.data.file.file_id}/download?inline=true`
        };

        uploadedFiles.push(uploadedFile);

        // Update file status
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'success' } : f
        ));

      } catch (error) {
        console.error('Upload error:', error);
        let errorMessage = error.response?.data?.error || error.message;

        // Provide more helpful messages for common errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
        } else if (error.response?.status === 413) {
          errorMessage = 'File too large. The server has a size limit. Please compress the file or contact support.';
        } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = 'Upload timeout. File may be too large or connection is slow. Try again with a better connection.';
        } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (!navigator.onLine) {
          errorMessage = 'No internet connection. Please check your network and try again.';
        } else if (error.message.includes('Authentication required')) {
          errorMessage = 'Session expired. Please refresh the page to log in again.';
        }

        uploadErrors.push(`${fileItem.name}: ${errorMessage}`);

        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'error', error: errorMessage } : f
        ));
      }
    }

    setUploading(false);

    // Report errors if any
    if (uploadErrors.length > 0) {
      setErrors(uploadErrors);
    }

    // Report successful uploads to parent
    if (uploadedFiles.length > 0 && onFilesUploaded) {
      onFilesUploaded(uploadedFiles);
      // Clear successful uploads after a delay
      setTimeout(() => {
        setFiles(prev => prev.filter(f => f.status === 'error'));
      }, 2000);
    }
  };

  /**
   * Clear all files and errors
   */
  const handleClear = () => {
    setFiles([]);
    setErrors([]);
  };

  return (
    <Box>
      {/* Dropzone */}
      <Paper
        {...getRootProps()}
        sx={{
          p: compact ? 2 : 4,
          textAlign: 'center',
          cursor: disabled || uploading ? 'default' : 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          '&:hover': disabled || uploading ? {} : { bgcolor: 'action.hover' },
          mb: 2
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ fontSize: compact ? 32 : 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant={compact ? 'body2' : 'h6'} gutterBottom>
          {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
        </Typography>
        <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary">
          or click to browse
        </Typography>
        {!compact && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Supported: Images, Videos, PDFs, Documents (Max: {formatBytes(MAX_FILE_SIZE)})
          </Typography>
        )}
      </Paper>

      {/* Error messages */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors([])}>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">{error}</Typography>
          ))}
        </Alert>
      )}

      {/* File list */}
      {files.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <List dense={compact}>
            {files.map((fileItem) => (
              <ListItem key={fileItem.id}>
                <ListItemIcon>
                  {fileItem.status === 'success' ? (
                    <CheckIcon color="success" />
                  ) : fileItem.status === 'error' ? (
                    <ErrorIcon color="error" />
                  ) : (
                    getFileIcon(fileItem.type)
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={fileItem.name}
                  secondary={
                    <>
                      {formatBytes(fileItem.size)}
                      {fileItem.status === 'pending' && fileItem.progress > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={fileItem.progress}
                          sx={{ mt: 1 }}
                        />
                      )}
                      {fileItem.error && (
                        <Typography variant="caption" color="error" display="block">
                          {fileItem.error}
                        </Typography>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  {fileItem.status === 'success' && (
                    <Chip label="Uploaded" color="success" size="small" />
                  )}
                  {!uploading && fileItem.status === 'pending' && (
                    <IconButton edge="end" onClick={() => removeFile(fileItem.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Upload buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              disabled={uploading || files.every(f => f.status !== 'pending')}
              fullWidth={compact}
            >
              {uploading ? 'Uploading...' : 'Upload Files'}
            </Button>
            {!uploading && (
              <Button
                variant="outlined"
                onClick={handleClear}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TicketAttachmentUpload;
